import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { LoginData, LoginError } from 'auth/models';
import { GENERIC_MESSAGES } from 'shared/constants/GenericMessages';
import { useAuthService } from 'auth/services/useAuthService';
import { useUserService } from 'user/services/useUserService';
import { Usuario } from 'user/models/Usuario';
import { LoginPhase } from 'navigation/models/login-phase';
import { useSecurity } from 'auth/hooks/useSecurity';
import { LOGIN_ERROR_MESSAGES } from 'auth/constants/LoginErrorMessages';
import { useRootLoadingSpinner } from 'shared/spinner';
import { useSingleFetch } from 'shared/http';
import { useNotifications } from 'shared/notifications';
import { config } from '../../app/config';
import {
  CONFIRMATION_EMAIL_PATH,
  PASSWORD_EXPIRED_PATH,
  RECOVER_PASSWORD_PATH,
  RESET_PASSWORD_PATH,
} from '../../user/routes';
import { useLocation, useNavigate } from 'react-router-dom';

interface AuthContextType {
  disableSignIn: boolean;
  user: Usuario;
  login: (username: string, password: string) => void;
  loginAsUser: (username: string) => void;
  logout: () => void;
  isLogged: () => boolean;
  updateUserData: () => void;
}

const contextInitialValue = {
  user: null,
  disableSignIn: false,
  login: async () => null,
  logout: () => {},
  loginAsUser: async () => null,
  isLogged: () => false,
  updateUserData: () => null,
};

export const AuthContext = createContext<AuthContextType>(contextInitialValue);

const determineLoginPhase = (user: Usuario): LoginPhase => {
  if (!user.mail || user.mail.trim() === '') {
    return LoginPhase.EMAIL_REQUIRED_BLOCKED;
  }

  // Si tiene email, mantener la lógica original
  const phase = {
    confirmed: LoginPhase.LOGGED_IN,
    unconfirmed: LoginPhase.LOGGED_IN,
  };
  if (!user.mailStatus) return LoginPhase.LOGGED_IN;
  return phase[user.mailStatus] ?? LoginPhase.LOGGED_IN;
};

const availableFunctions = config.availableFunctions;

export const AuthProvider = ({ children }) => {
  const { accessToken, saveTokens, removeTokens, saveLoginPhase } = useSecurity();
  const [user, setUser] = useState<Usuario>(
    accessToken
      ? ({
          funcionalidades: ['CARGANDO'],
        } as any)
      : null,
  );
  const { setRootLoadingSpinnerStatus } = useRootLoadingSpinner();
  const [disableSignIn, setDisableSignIn] = useState<boolean>(false);
  const navigate = useNavigate();
  const authService = useAuthService();
  const userService = useUserService();
  const { addNotification } = useNotifications();
  const location = useLocation();
  const singleFetch = useSingleFetch();
  const isPasswordExpired = useRef(false);
  const isLoginAciton = useRef(false);

  const updateUser = useCallback(() => {
    singleFetch(() =>
      userService
        .getAuthenticatedUserData()
        .then((response) => {
          setUser({
            ...response,
            // Filtramos las opciones disponibles
            funcionalidades: [
              ...availableFunctions.filter((fn) =>
                response.funcionalidades?.includes(fn),
              ),
              'PERMITIR',
            ],
          });
          if (isLoginAciton.current && !isPasswordExpired.current) {
            const loginPhase = determineLoginPhase(response);
            saveLoginPhase(loginPhase);
            isLoginAciton.current = false;
          }
        })
        .catch(() => {
          addNotification(GENERIC_MESSAGES.authenticationError, 'error');
          setUser(null);
        }),
    );
  }, [saveLoginPhase, userService, singleFetch, addNotification]);

  const isPublicPath = (pathname: string) => {
    const publicPaths = [
      CONFIRMATION_EMAIL_PATH.split('/:')[0],
      RECOVER_PASSWORD_PATH,
      RESET_PASSWORD_PATH.split('/:')[0],
    ];
    return publicPaths.some((path) => pathname.startsWith(path));
  };

  useEffect(() => {
    if (isPublicPath(location.pathname)) {
      return; //para la ejecucion del useEffect solo cuando no sea una ruta publica
    }
    if (accessToken) {
      updateUser();
    } else {
      setUser(null);
    }
  }, [updateUser, accessToken, location.pathname]);

  const handleUserLogin = (userData: LoginData) => {
    isLoginAciton.current = true;
    saveTokens(userData);
    if (userData?.credentialsExpired) {
      isPasswordExpired.current = true;
      saveLoginPhase(LoginPhase.EXPIRED_PASSWORD);
      navigate(PASSWORD_EXPIRED_PATH);
    } else {
      isPasswordExpired.current = false;
    }
  };

  const contextValue: AuthContextType = {
    disableSignIn,
    user,
    login(username, password) {
      setDisableSignIn(true);
      setRootLoadingSpinnerStatus(true);
      authService
        .doLogin(username, password)
        .then((response) => {
          handleUserLogin(response.body);
        })
        .catch((e) => {
          const msg = e instanceof LoginError ? e.message : GENERIC_MESSAGES.genericError;
          removeTokens();
          addNotification(msg, 'error');
        })
        .finally(() => {
          setRootLoadingSpinnerStatus(false);
          setDisableSignIn(false);
        });
    },
    loginAsUser(username) {
      if (user == null || user.tipoUsuario !== 'PRISMA') {
        addNotification(LOGIN_ERROR_MESSAGES.Unauthorized, 'error');
      } else {
        setRootLoadingSpinnerStatus(true);
        authService
          .doLoginAsUser(username)
          .then(
            (response) => handleUserLogin(response.body),
            (e) => {
              const msg =
                e instanceof LoginError ? e.message : GENERIC_MESSAGES.genericError;
              addNotification(msg, 'error');
            },
          )
          .finally(() => setRootLoadingSpinnerStatus(false));
      }
    },
    logout: useCallback(() => {
      authService.doLogout().catch((e) => {});
      removeTokens();
      saveLoginPhase(LoginPhase.LOGIN);
    }, [authService, removeTokens, saveLoginPhase]),
    isLogged: useCallback(() => {
      return Boolean(user);
    }, [user]),
    updateUserData: useCallback(() => {
      setTimeout(() => {
        if (accessToken) {
          updateUser();
        }
      });
    }, [updateUser, accessToken]),
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
